/* eslint-disable max-len */
import React, { forwardRef, useContext, useEffect, useState } from 'react';
import { FormControl, Grid, InputLabel, Stack, TextField, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DateTimePicker } from '@mui/x-date-pickers';
import DurationField from '../../../../../UiComponents/Duration/Duration';
import { LanguageContext } from '../../../../../libs/Context';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

interface ScheduleDatezoneProps
{
    formDate: any;
    handleUpdateDate: (dt: any) => void;
    HandleDuration: (val: any) => void;
    defaultDuration?: string;
    selectedTimezone?: string;
}

const ScheduleDatezone = forwardRef((props: ScheduleDatezoneProps, ref:any) =>
{
	const { formDate, handleUpdateDate, defaultDuration, HandleDuration, selectedTimezone = 'Asia/Kolkata' } = props;
	const { language } = useContext(LanguageContext);

	const [ localDate, setLocalDate ] = useState<Dayjs | null>(null);

	// Convert UTC date to local timezone for display
	useEffect(() =>
	{
		if (formDate)
		{
			// If formDate is already a dayjs object, convert it to the selected timezone
			// First ensure it's in UTC, then convert to the selected timezone
			let utcDate;

			if (dayjs.isDayjs(formDate))
			{
				// If it's already a dayjs object, check if it's in UTC
				utcDate = formDate.utc();
			}
			else
			{
				// If it's a timestamp or other format, convert to UTC
				utcDate = dayjs(formDate).utc();
			}

			const dateInTimezone = utcDate.tz(selectedTimezone);

			setLocalDate(dateInTimezone);
		}
		else
		{
			// If no formDate, set current time + 5 minutes in selected timezone
			const nowInTimezone = dayjs().tz(selectedTimezone).add(5, 'minute');

			setLocalDate(nowInTimezone);
		}
	}, [ formDate, selectedTimezone ]);

	const handleChange = (newValue: Dayjs | null) =>
	{
		if (newValue)
		{
			// Convert the selected time in the timezone to UTC for backend
			// newValue is already in the selected timezone, so we just convert to UTC
			const utcDate = newValue.utc();

			handleUpdateDate(utcDate);
		}
		else
		{
			const nowInTimezone = dayjs().tz(selectedTimezone).add(5, 'minute');
			setLocalDate(nowInTimezone);
			handleUpdateDate(nowInTimezone.utc());
		}
	};

	return (
		<Grid container spacing={1.5} mt='var(--pixel4)'>
			<Grid item xs={12} sm={6} lg={6}>
				<Stack>
					<InputLabel sx={{ fontSize: 'var(--pixel14)', color: 'var(--black)' }}>
						{language?.dateandtime} *</InputLabel>
					<FormControl sx={{ width: 'var(--percent100)' }} size='small'>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DateTimePicker
								value={localDate}
								className='fullWidth datetimeTxt'
								inputFormat='DD/MM/YYYY hh:mm a'
								onChange={handleChange}
								disablePast
								minDate={dayjs().tz(selectedTimezone)}
								renderInput={(params) => (
									<TextField {...params} size='small' margin='dense'
										sx={{
											'& .MuiSvgIcon-root' : {
												fontSize : 'var(--pixel20)'
											},
											'& MuiInputBase-inpu' : {
												padding : '8px 0'
											}
										}}
									/>)}
							/>
						</LocalizationProvider>
					</FormControl>
				</Stack>
			</Grid>
			<Grid item xs={12} sm={6} lg={6}>
				<Stack>
					<InputLabel sx={{ fontSize: 'var(--pixel14)', color: 'var(--black)' }}>{language?.duration}</InputLabel>
					<FormControl sx={{ width: 'var(--percent100)' }} size='small'>
						<DurationField ref={ref} defaultDuration={defaultDuration || '30'} HandleDuration={HandleDuration} callFunc/>
					</FormControl>
				</Stack>
			</Grid>
		</Grid>
	);
});

ScheduleDatezone.displayName = 'ScheduleDatezone';

export default ScheduleDatezone;